/// The quarter goal card, first on the funnel dashboard (design 2026-11-02):
/// $750 of net proceeds from Nov 1 to Jan 31, the pace verdict, the pace
/// chart over the whole quarter, the two drivers, the three guardrails and a
/// quiet footer. Every number and status comes from the backend's quarterGoal
/// block (functions/src/quarterGoalCore.ts); the words come from
/// src/util/quarterGoalCopy.ts. Kept in its own file, as DiscoveryCard is, so
/// it renders without the dashboard's Firebase wiring.
import {
  CALLS_LINE,
  GUARDRAIL_NOTE,
  STATUS_WORD,
  brokenLines,
  cardNote,
  chartLabel,
  d1Row,
  lagLines,
  metaLine,
  purchasesTile,
  ratingsReading,
  refundsReading,
  salesText,
  searchTile,
  usd0,
  verdict,
  watchLine,
  type DriverTileCopy,
  type GuardrailStatus,
  type QuarterGoalLive,
  type Tone,
} from '../util/quarterGoalCopy'

export type { QuarterGoalLive } from '../util/quarterGoalCopy'

interface FencePost {
  date: string
  type: string
  label: string
}

const TONE: Record<Tone, string> = {
  emerald: 'text-emerald-300',
  amber: 'text-amber-300',
  rose: 'text-rose-300',
}

// The dashboard's fence-post colours (DashboardScreen's fenceColor).
const fenceColor = (type: string) => (type === 'asa' ? '#fbbf24' : type === 'campaign' ? '#34d399' : '#60a5fa')

const daysFrom = (a: string, b: string) => Math.round((Date.parse(`${b}T00:00:00Z`) - Date.parse(`${a}T00:00:00Z`)) / 86_400_000)

/// The whole quarter on one axis: even pace to the target (dashed), the
/// reported cumulative proceeds (solid), and a hatched strip over the days
/// Apple has not reported yet.
function PaceChart({ q, fencePosts }: { q: QuarterGoalLive; fencePosts: FencePost[] }) {
  const p = q.proceeds
  const days = q.quarter.days
  const W = 600
  const H = 112
  const top = 4
  const bot = 4
  const yMax = Math.max(p.targetUSD, p.sumUSD)
  const x = (t: number) => (t / days) * W
  const y = (v: number) => H - bot - (v / yMax) * (H - top - bot)
  // Day n's cumulative sum sits at the end of day n.
  const pts: [number, number][] = [[0, 0], ...p.series.map((s): [number, number] => [daysFrom(q.quarter.start, s.date) + 1, s.cumUSD])]
  const line = pts.map(([t, v], i) => `${i ? 'L' : 'M'}${x(t).toFixed(1)} ${y(v).toFixed(1)}`).join(' ')
  const last = pts[pts.length - 1]!
  const area = `${line} L${x(last[0]).toFixed(1)} ${y(0).toFixed(1)} Z`
  const reportedTo = p.reportedThrough ? daysFrom(q.quarter.start, p.reportedThrough) + 1 : 0
  const today = Math.min(days, q.quarter.dayOfQuarter)
  const hatchFrom = x(reportedTo)
  const hatchTo = x(today)
  const showHatch = q.quarter.phase !== 'final' && hatchTo > hatchFrom
  const decIdx = daysFrom(q.quarter.start, `${q.quarter.start.slice(0, 4)}-12-01`)
  const janIdx = daysFrom(q.quarter.start, q.quarter.end.slice(0, 4) + '-01-01')
  const marks = fencePosts.filter((f) => f.date >= q.quarter.start && f.date <= q.quarter.end)
  const id = 'qg'
  return (
    <div className="mt-4">
      <div className="mb-1 flex justify-end text-[10px] tabular-nums text-slate-500">{usd0(p.targetUSD)}</div>
      <div className="relative h-[112px]">
        <svg
          viewBox={`0 0 ${W} ${H}`}
          preserveAspectRatio="none"
          className="absolute inset-0 h-full w-full"
          role="img"
          aria-label={chartLabel(q)}
        >
          <defs>
            <pattern id={`${id}h`} width="5" height="5" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
              <line x1="0" y1="0" x2="0" y2="5" stroke="#475569" strokeWidth="2" />
            </pattern>
          </defs>
          {[decIdx, janIdx].map((t) => (
            <line key={t} x1={x(t)} x2={x(t)} y1={0} y2={H} stroke="#1e293b" strokeWidth="1" vectorEffect="non-scaling-stroke" />
          ))}
          <line x1={0} x2={W} y1={y(0)} y2={y(0)} stroke="#334155" strokeWidth="1" vectorEffect="non-scaling-stroke" />
          {showHatch && <rect x={hatchFrom} y={0} width={hatchTo - hatchFrom} height={H} fill={`url(#${id}h)`} opacity="0.5" />}
          {marks.map((m, i) => {
            const fx = x(daysFrom(q.quarter.start, m.date))
            return (
              <line key={i} x1={fx} x2={fx} y1={0} y2={H} stroke={fenceColor(m.type)} strokeWidth="1" strokeDasharray="2 2" opacity="0.7" vectorEffect="non-scaling-stroke">
                <title>{`${m.date.slice(5)} ${m.label}`}</title>
              </line>
            )
          })}
          <line
            x1={x(0)}
            y1={y(0)}
            x2={x(days)}
            y2={y(p.targetUSD)}
            stroke="#64748b"
            strokeWidth="1.5"
            strokeDasharray="5 4"
            vectorEffect="non-scaling-stroke"
          />
          {pts.length > 1 && (
            <>
              <path d={area} fill="#38bdf8" opacity="0.08" />
              <path d={line} fill="none" stroke="#38bdf8" strokeWidth="2" strokeLinejoin="round" vectorEffect="non-scaling-stroke" />
              {/* A zero-length round-capped path: a dot that stays round in a stretched SVG. */}
              <path
                d={`M${x(last[0]).toFixed(1)} ${y(last[1]).toFixed(1)} l0 0`}
                stroke="#38bdf8"
                strokeWidth="7"
                strokeLinecap="round"
                vectorEffect="non-scaling-stroke"
              />
            </>
          )}
        </svg>
      </div>
      <div className="relative mt-1 h-3 text-[10px] text-slate-500">
        <span className="absolute left-0">Nov</span>
        <span className="absolute" style={{ left: `${(decIdx / days) * 100}%` }}>Dec</span>
        <span className="absolute" style={{ left: `${(janIdx / days) * 100}%` }}>Jan</span>
      </div>
      <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-[10px] text-slate-500">
        <span className="inline-flex items-center gap-1.5">
          <svg width="16" height="6" aria-hidden="true"><line x1="0" y1="3" x2="16" y2="3" stroke="#38bdf8" strokeWidth="2" /></svg>
          Reported
        </span>
        <span className="inline-flex items-center gap-1.5">
          <svg width="16" height="6" aria-hidden="true"><line x1="0" y1="3" x2="16" y2="3" stroke="#64748b" strokeWidth="1.5" strokeDasharray="4 3" /></svg>
          Even pace to {usd0(p.targetUSD)}
        </span>
        <span className="inline-flex items-center gap-1.5">
          <svg width="12" height="10" aria-hidden="true">
            <defs>
              <pattern id={`${id}l`} width="4" height="4" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
                <line x1="0" y1="0" x2="0" y2="4" stroke="#64748b" strokeWidth="1.5" />
              </pattern>
            </defs>
            <rect width="12" height="10" fill={`url(#${id}l)`} />
          </svg>
          Not reported yet
        </span>
      </div>
    </div>
  )
}

function DriverTile({ copy }: { copy: DriverTileCopy }) {
  return (
    <div className="rounded-lg bg-slate-950/40 p-3 ring-1 ring-white/5">
      <div className="text-[11px] uppercase tracking-wide text-slate-400">{copy.label}</div>
      <div className="mt-1 flex items-baseline gap-2">
        <span className={copy.fillPct == null ? 'text-lg font-semibold text-slate-400' : 'text-2xl font-bold tabular-nums'}>{copy.value}</span>
        {copy.aim && <span className="text-xs text-slate-400">{copy.aim}</span>}
      </div>
      {copy.fillPct != null && (
        <div className="mt-2 h-1.5 overflow-hidden rounded bg-slate-800" role="img" aria-label={`${copy.value} ${copy.aim ?? ''}`}>
          <div className="h-full rounded bg-sky-500" style={{ width: `${copy.fillPct.toFixed(1)}%` }} />
        </div>
      )}
      <div className="mt-1.5 text-[11px] text-slate-500">{copy.sub}</div>
    </div>
  )
}

function StatusWord({ status }: { status: GuardrailStatus }) {
  if (status === 'broken') {
    return (
      <span className="rounded bg-rose-500/20 px-1.5 py-0.5 font-semibold text-rose-200 ring-1 ring-rose-400/40">
        {STATUS_WORD.broken}
      </span>
    )
  }
  return <span className={status === 'holding' ? 'text-emerald-300' : 'text-slate-500'}>{STATUS_WORD[status]}</span>
}

function GuardRow({ name, reading, limit, status }: { name: string; reading: string; limit: string; status: GuardrailStatus }) {
  return (
    <div className="grid grid-cols-[1fr_auto] gap-x-3 gap-y-0.5 border-t border-white/5 py-1.5 text-xs sm:grid-cols-[12rem_1fr_7.5rem_5.5rem] sm:items-baseline">
      <div className="text-slate-300">{name}</div>
      <div className="text-right sm:order-last">
        <StatusWord status={status} />
      </div>
      <div className="tabular-nums text-slate-200">{reading}</div>
      <div className="text-right text-slate-500 sm:text-left">{limit}</div>
    </div>
  )
}

export function QuarterGoalCard({ data: q, fencePosts = [] }: { data: QuarterGoalLive; fencePosts?: FencePost[] }) {
  const p = q.proceeds
  const v = verdict(q)
  const broken = brokenLines(q)
  const lag = lagLines(q)
  const d1 = d1Row(q)
  const g = q.guardrails
  const watch = watchLine(q)
  return (
    <section className="rounded-lg bg-slate-900 p-4 ring-1 ring-white/10" aria-label="Quarter goal">
      <div className="mb-3 flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="text-sm font-semibold">Quarter goal</h2>
        <span className="text-[11px] text-slate-500">{metaLine(q)}</span>
      </div>

      <div className="grid gap-3 sm:grid-cols-[1fr_minmax(0,24rem)] sm:items-start">
        <div>
          <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
            <span className="text-4xl font-bold tabular-nums tracking-tight">{usd0(p.sumUSD)}</span>
            <span className="text-base text-slate-400">of {usd0(p.targetUSD)}</span>
            <span className="ml-1 text-sm tabular-nums text-slate-300">{salesText(p.sales)}</span>
          </div>
          <p className="mt-1 text-[11px] text-slate-500">Net proceeds from every storefront, in USD</p>
        </div>
        <div className="sm:text-right">
          <div className={`text-lg font-semibold leading-tight ${TONE[v.tone]}`}>{v.word}</div>
          <p className="mt-0.5 text-xs text-slate-300">{v.line1}</p>
          {v.line2 && <p className="text-xs text-slate-400">{v.line2}</p>}
        </div>
      </div>

      {broken.length > 0 && (
        <div className="mt-3 space-y-0.5 rounded-md bg-rose-500/10 px-3 py-2 text-xs text-rose-100 ring-1 ring-rose-400/40">
          {broken.map((b) => (
            <p key={b}>{b}</p>
          ))}
        </div>
      )}

      <PaceChart q={q} fencePosts={fencePosts} />
      {lag.length > 0 && <p className="mt-2 text-[11px] text-slate-400">{lag.join(' ')}</p>}

      <div className="mt-4 border-t border-white/5 pt-3">
        <h3 className="mb-2 text-xs font-semibold text-slate-300">What moves it</h3>
        <div className="grid gap-3 sm:grid-cols-2">
          <DriverTile copy={purchasesTile(q)} />
          <DriverTile copy={searchTile(q)} />
        </div>
      </div>

      <div className="mt-4 border-t border-white/5 pt-3">
        <h3 className="mb-1 text-xs font-semibold text-slate-300">Guardrails</h3>
        <GuardRow name={d1.name} reading={d1.reading} limit={`at least ${g.d1.floorPct}%`} status={g.d1.status} />
        <GuardRow name="Ratings this quarter" reading={ratingsReading(q)} limit={`${g.ratings.floorAvg} or more`} status={g.ratings.status} />
        <GuardRow name="Refunds this quarter" reading={refundsReading(q)} limit={`${g.refunds.ceilingPct}% at most`} status={g.refunds.status} />
        <p className="mt-1.5 text-[11px] text-slate-500">{GUARDRAIL_NOTE}</p>
      </div>

      <div className="mt-4 space-y-1.5 border-t border-white/5 pt-3 text-[11px]">
        {watch && <p className="text-slate-400">{watch}</p>}
        <p className="text-slate-400">{CALLS_LINE}</p>
        <p className="text-slate-500">{cardNote(q)}</p>
      </div>
    </section>
  )
}
